/* eslint-disable import/no-cycle */
import {
  useCallback,
} from 'react';
import type { AgentTeamProductExt, SuperProduct } from '$/api/server';
import { getAgentTeamSubscriptionProducts } from '$/domains/teamAgent/teamAgentService';
import type {
  AgentTeamSubscriptionProductsParams,
  AgentTeamSubscriptionProductsResult,
} from '$/domains/teamAgent/type';
import type {
  VipModalTrackParams,
  VipProductEntranceScene,
} from '$/store/useVipModalStore';
import useSubscribeTeamProductList, {
  deepKeysToCamelCase,
} from './useSubscribeTeamProductList';

interface SubscribeVipProductListOptions {
  useTeamHeaders?: boolean;
  entranceScene?: VipProductEntranceScene;
  trackParams?: VipModalTrackParams;
}

interface VipProductExtraState {
  productExt?: AgentTeamProductExt;
  entranceScene?: number;
  subBtnText: string;
}

const VIP_PRODUCT_EXTRA_INITIAL_STATE: VipProductExtraState = {
  productExt: undefined,
  entranceScene: undefined,
  subBtnText: '',
};

/**
 * 归一化会员商品列表 hook 入参，兼容 master 直接传 trackParams 与 feat 传 options 对象。
 */
function normalizeSubscribeVipProductListOptions(
  options?: SubscribeVipProductListOptions | VipModalTrackParams,
): SubscribeVipProductListOptions {
  if (!options) return {};
  if ('useTeamHeaders' in options) {
    return options;
  }
  return { trackParams: options };
}

/**
 * 拉取会员订阅商品列表，并复用订阅 SDK 生成后续支付需要的 transferId。
 */
function useSubscribeVipProductList(
  options?: SubscribeVipProductListOptions | VipModalTrackParams,
) {
  const {
    useTeamHeaders = false,
    entranceScene = 0,
    trackParams,
  } = normalizeSubscribeVipProductListOptions(options);

  /**
   * 将会员订阅接口结果转换成弹窗展示和支付链路消费的结构。
   */
  const mapSubscriptionProductResult = useCallback((vipinfo: AgentTeamSubscriptionProductsResult) => {
    return {
      productList: deepKeysToCamelCase(vipinfo?.products || []) as unknown as SuperProduct[],
      extraState: {
        productExt: vipinfo?.product_ext
          ? deepKeysToCamelCase(vipinfo.product_ext) as AgentTeamProductExt
          : undefined,
        entranceScene: Number(vipinfo?.entrance_scene) || undefined,
        subBtnText: vipinfo?.sub_btn_text || '',
      },
    };
  }, []);

  /**
   * 按当前会员 tab 补充团队商品模式和入口场景，并控制是否携带固定组织请求头。
   */
  const fetchSubscriptionProducts = useCallback((params: AgentTeamSubscriptionProductsParams) => {
    return getAgentTeamSubscriptionProducts({
      ...params,
      ...(useTeamHeaders ? {
        mode: 'team' as const,
        entrance_scene: entranceScene,
      } : {}),
    }, { useTeamHeaders });
  }, [entranceScene, useTeamHeaders]);

  const {
    transferId,
    productList,
    extraState,
    refetch,
    loading,
    error,
  } = useSubscribeTeamProductList({
    trackParams,
    includeRebateCode: true,
    initialExtraState: VIP_PRODUCT_EXTRA_INITIAL_STATE,
    errorMessage: 'Failed to fetch product list:',
    fetchProducts: fetchSubscriptionProducts,
    mapResult: mapSubscriptionProductResult,
  });

  return {
    transferId,
    productList,
    productExt: extraState.productExt,
    entranceScene: extraState.entranceScene,
    subBtnText: extraState.subBtnText,
    refetch,
    loading,
    error,
  };
}

export default useSubscribeVipProductList;
